import React from 'react';
import { PageSection, SiteData } from '../../../types';
import { Linkedin, Mail, Phone, Twitter, ExternalLink } from 'lucide-react';
import { isDarkBackground, getOptimalTextColor, getOptimalMutedTextColor, getCardStylesForBackground } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
}

export const TeamSection: React.FC<SectionProps> = ({ section, siteData }) => {
  const content = section.content || {};
  const style = section.style || {};

  const badge = content.badge || 'LİDERLİK KADROSU';
  const title = content.title || 'Deneyimli ve Vizyoner Ekibimiz';
  const description = content.description || '';
  const columns = content.columns || 3;

  const rawItems = Array.isArray(content.items) ? content.items : [];

  const bgColor = style.backgroundColor || (style as any)?.bgColor || '#FFFFFF';
  const isDark = isDarkBackground(bgColor);
  const textColor = style.textColor || getOptimalTextColor(bgColor);
  const mutedTextColor = getOptimalMutedTextColor(bgColor);
  const cardStyles = getCardStylesForBackground(bgColor);
  const accentColor = siteData.settings?.accentColor || '#2563EB';

  const gridColsClass =
    columns === 2
      ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto'
      : columns === 4
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
      : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <section
      id={style.anchorId || style.customId || 'team-section'}
      className="py-20 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="max-w-7xl mx-auto space-y-12">
        <div className="text-center max-w-2xl mx-auto space-y-3">
          {badge && (
            <div
              className={`inline-block px-3.5 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider ${
                isDark ? 'bg-white/10' : 'bg-black/5'
              }`}
              style={{ color: accentColor }}
            >
              {badge}
            </div>
          )}
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{title}</h2>
          {description && (
            <p className="text-sm leading-relaxed" style={{ color: mutedTextColor }}>
              {description}
            </p>
          )}
        </div>

        {rawItems.length === 0 ? (
          <div className="p-12 text-center rounded-3xl border-2 border-dashed border-slate-200 dark:border-slate-800 max-w-md mx-auto space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 flex items-center justify-center mx-auto text-xl">
              👥
            </div>
            <div className="space-y-1">
              <h3 className={`text-sm font-bold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>Henüz Ekip Üyesi Eklenmedi</h3>
              <p className="text-xs text-slate-400">
                Sol panelden ekip üyelerinizi ve unvanlarını ekleyerek şirket kadronuzu tanıtın.
              </p>
            </div>
          </div>
        ) : (
          <div className={`grid ${gridColsClass} gap-8`}>
          {rawItems.map((member: any, idx: number) => {
            const memberName = member.name || member.title || 'Ekip Üyesi';
            const memberRole = member.role || member.subtitle || 'Uzman';
            const memberBio = member.bio || member.description || '';
            const memberAvatar =
              member.avatar ||
              member.imageUrl ||
              'https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=400&auto=format&fit=crop';
            const socials = member.socials || {};

            return (
              <div
                key={member.id || idx}
                className="p-6 rounded-3xl transition-all duration-200 text-center flex flex-col justify-between hover:-translate-y-1 hover:shadow-xl"
                style={{
                  backgroundColor: cardStyles.cardBg,
                  border: `1px solid ${cardStyles.cardBorder}`,
                  color: cardStyles.textPrimary,
                }}
              >
                <div className="space-y-4">
                  <div className="w-28 h-28 mx-auto rounded-full overflow-hidden border-4 shadow-md transition group-hover:scale-105"
                    style={{ borderColor: isDark ? 'rgba(255,255,255,0.15)' : '#FFFFFF' }}>
                    <img
                      src={memberAvatar}
                      alt={memberName}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-base tracking-tight">{memberName}</h3>
                    <p
                      className="text-xs font-bold uppercase tracking-wide mt-1"
                      style={{ color: accentColor }}
                    >
                      {memberRole}
                    </p>
                    {memberBio && (
                      <p
                        className="text-xs mt-3 leading-relaxed"
                        style={{ color: cardStyles.textMuted }}
                      >
                        {memberBio}
                      </p>
                    )}
                  </div>
                </div>

                {/* Social Links */}
                {(socials.linkedin || socials.email || socials.twitter || socials.phone) && (
                  <div className="flex items-center justify-center gap-3 pt-5 mt-4 border-t"
                    style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}>
                    {socials.linkedin && (
                      <a
                        href={socials.linkedin.startsWith('http') ? socials.linkedin : `https://${socials.linkedin}`}
                        target="_blank"
                        rel="noreferrer"
                        title="LinkedIn"
                        className="p-2 rounded-xl transition hover:scale-110"
                        style={{
                          backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                          color: accentColor,
                        }}
                      >
                        <Linkedin className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {socials.email && (
                      <a
                        href={`mailto:${socials.email}`}
                        title="E-posta"
                        className="p-2 rounded-xl transition hover:scale-110"
                        style={{
                          backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                          color: cardStyles.textMuted,
                        }}
                      >
                        <Mail className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {socials.twitter && (
                      <a
                        href={socials.twitter.startsWith('http') ? socials.twitter : `https://${socials.twitter}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Twitter / X"
                        className="p-2 rounded-xl transition hover:scale-110"
                        style={{
                          backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                          color: cardStyles.textMuted,
                        }}
                      >
                        <Twitter className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {socials.phone && (
                      <a
                        href={`tel:${socials.phone.replace(/[^0-9+]/g, '')}`}
                        title="Telefon"
                        className="p-2 rounded-xl transition hover:scale-110"
                        style={{
                          backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                          color: cardStyles.textMuted,
                        }}
                      >
                        <Phone className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>
    </section>
  );
};
